import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { Box, Chip, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AssessmentIcon from '@material-ui/icons/Assessment';
import EuroIcon from '@material-ui/icons/Euro';
import TimelineIcon from '@material-ui/icons/Timeline';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

type GapStatus = 'ready' | 'missing' | 'gated';

type GapCheck = {
  label: string;
  status: GapStatus;
  evidence: string;
  requiredWork: string;
};

type OperationalLink = {
  label: string;
  to: string;
  enabled: boolean;
};

type AzureBillingProduct = {
  productId: string;
  title: string;
  namespace: string;
  currency: 'EUR';
  allocationWeight: number;
  cpuCores: number;
  memoryGiB: number;
  podCount: number;
  allocatedCost: number;
};

type AzureBillingSummary = {
  generatedAt: string;
  state: 'ready' | 'partial' | 'unavailable';
  source: 'azure-cost-resource-groups';
  currency: 'EUR';
  period: {
    from: string;
    to: string;
  };
  allocation: {
    method: string;
    evidence: string;
  };
  products: AzureBillingProduct[];
  resourceGroups: Array<{
    resourceGroup: string;
    currency: 'EUR';
    cost: number;
    from: string;
    to: string;
  }>;
  unallocated: {
    currency: 'EUR';
    cost: number;
    reason: string;
  };
  warnings: string[];
};

type ProjectInsight = {
  id: string;
  apiName: string;
  title: string;
  description: string;
  owner: string;
  maturity: string;
  maxFeatureClass: string;
  blueprintStatus: string;
  firstVerticalSlice: string;
  proofLevel: string;
  namespace: string;
  runtimeMappingMode: string;
  runtimeChildComponents: string[];
  lighthouseAuditMode: string;
  lighthouseChildComponents: string[];
  lighthouseEvidence: string;
  workflow: string;
  releaseMode: string;
  healthEndpoint: string;
  readyEndpoint: string;
  primaryController: string;
  validationCommands: string[];
  blockers: string[];
  gapChecks: GapCheck[];
  operationalLinks: OperationalLink[];
};

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'linear-gradient(135deg, rgba(0,121,107,0.18), rgba(251,140,0,0.12))',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  projectCard: {
    height: '100%',
  },
  statCard: {
    height: '100%',
  },
  list: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  listItem: {
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  metaGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    marginTop: theme.spacing(2),
  },
  metaItem: {
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  gapGrid: {
    display: 'grid',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(2),
  },
  gapItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  linkRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
}));

function readString(value: unknown, emptyValue: string): string {
  return typeof value === 'string' && value.length > 0 ? value : emptyValue;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => (typeof item === 'string' ? item : undefined))
    .filter((item): item is string => Boolean(item));
}

function readAnnotation(
  annotations: Record<string, string> | undefined,
  key: string,
  emptyValue = 'not declared',
): string {
  return readString(annotations?.[key], emptyValue);
}

function splitAnnotationList(value: string): string[] {
  if (!value || value === 'not declared') {
    return [];
  }

  return value
    .split('|')
    .map(item => item.trim())
    .filter(Boolean);
}

function isDeclared(value: string): boolean {
  return value !== 'not declared' && value.length > 0;
}

function hasAnnotation(
  annotations: Record<string, string>,
  key: string,
): boolean {
  return isDeclared(readAnnotation(annotations, key));
}

function hasLiveIntegrationGate(
  annotations: Record<string, string>,
  integration: string,
): boolean {
  return (
    readAnnotation(annotations, `c4ci.io/live-integration.${integration}`, '')
      .toLowerCase()
      .trim() === 'true'
  );
}

function createGapCheck(
  label: string,
  ready: boolean,
  evidence: string,
  requiredWork: string,
): GapCheck {
  return {
    label,
    status: ready ? 'ready' : 'missing',
    evidence,
    requiredWork,
  };
}

function createGatedCheck(
  label: string,
  evidence: string,
  requiredWork: string,
): GapCheck {
  return {
    label,
    status: 'gated',
    evidence,
    requiredWork,
  };
}

function projectEntityToInsight(
  entity: Entity,
  apiRepoIds: Set<string>,
): ProjectInsight | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const repoId = annotations['c4ci.io/registry-repo-id'];
  if (typeof repoId !== 'string') {
    return undefined;
  }

  const apiName = `${repoId}-api`;
  const providesApis = readStringArray(entity.spec?.providesApis);
  const owner = readString(entity.spec?.owner, 'unassigned');
  const healthEndpoint = readAnnotation(annotations, 'c4ci.io/health-endpoint');
  const readyEndpoint = readAnnotation(annotations, 'c4ci.io/ready-endpoint');
  const validationCommands = splitAnnotationList(
    readAnnotation(annotations, 'c4ci.io/validation-commands', ''),
  );
  const namespace = readAnnotation(annotations, 'c4ci.io/release-namespace');
  const workflow = readAnnotation(annotations, 'c4ci.io/deploy-workflow');
  const runtimeMappingMode = readAnnotation(
    annotations,
    'c4ci.io/runtime-mapping-mode',
  );
  const runtimeChildComponents = splitAnnotationList(
    readAnnotation(annotations, 'c4ci.io/runtime-child-components', ''),
  );
  const lighthouseAuditMode = readAnnotation(
    annotations,
    'c4ci.io/lighthouse-audit-mode',
  );
  const lighthouseChildComponents = splitAnnotationList(
    readAnnotation(annotations, 'c4ci.io/lighthouse-child-components', ''),
  );
  const hasApi = providesApis.length > 0 || apiRepoIds.has(repoId);
  const hasTechDocs = hasAnnotation(annotations, 'backstage.io/techdocs-ref');
  const hasSource =
    hasAnnotation(annotations, 'github.com/project-slug') &&
    hasAnnotation(annotations, 'backstage.io/source-location');
  const hasDeployment = isDeclared(namespace) && isDeclared(workflow);
  const hasHealth = isDeclared(healthEndpoint) && isDeclared(readyEndpoint);
  const hasDirectKubernetes =
    hasAnnotation(annotations, 'backstage.io/kubernetes-namespace') &&
    hasAnnotation(annotations, 'backstage.io/kubernetes-label-selector');
  const hasChildKubernetes =
    runtimeMappingMode === 'component-entities' &&
    runtimeChildComponents.length > 0;
  const hasKubernetes = hasDirectKubernetes || hasChildKubernetes;
  const hasLighthouseSource =
    hasAnnotation(annotations, 'lighthouse.com/website-url') ||
    (lighthouseAuditMode === 'component-entities' &&
      lighthouseChildComponents.length > 0);
  const hasLighthouse =
    hasLighthouseSource && hasLiveIntegrationGate(annotations, 'lighthouse');
  const lighthouseEvidence = readAnnotation(
    annotations,
    'lighthouse.com/website-url',
    lighthouseChildComponents.join(', ') || lighthouseAuditMode,
  );
  const hasPagerDutyPath =
    hasAnnotation(annotations, 'pagerduty.com/service-id') &&
    hasLiveIntegrationGate(annotations, 'pagerduty');
  const hasSentryPath =
    hasAnnotation(annotations, 'sentry.io/project-slug') &&
    hasLiveIntegrationGate(annotations, 'sentry');
  const hasIncidentPath = hasPagerDutyPath || hasSentryPath;
  const hasRunbook = hasAnnotation(annotations, 'c4ci.io/runbook-url');
  const hasDashboard = hasAnnotation(annotations, 'c4ci.io/dashboard-url');
  const hasAlerts = hasAnnotation(annotations, 'c4ci.io/alerts-url');
  const hasCostAdapter = hasAnnotation(
    annotations,
    'c4ci.io/cost-adapter-source',
  );
  let kubernetesLink = '';
  if (hasDirectKubernetes) {
    kubernetesLink = `/catalog/default/component/${repoId}/kubernetes`;
  } else if (runtimeChildComponents[0]) {
    kubernetesLink = `/catalog/default/component/${runtimeChildComponents[0]}/kubernetes`;
  }

  let lighthouseLink = '';
  if (
    lighthouseAuditMode === 'component-entities' &&
    lighthouseChildComponents[0]
  ) {
    lighthouseLink = `/catalog/default/component/${lighthouseChildComponents[0]}/lighthouse`;
  } else if (hasAnnotation(annotations, 'lighthouse.com/website-url')) {
    lighthouseLink = `/catalog/default/component/${repoId}/lighthouse`;
  }

  const gapChecks: GapCheck[] = [
    createGapCheck(
      'Owner',
      isDeclared(owner) && owner !== 'guest' && owner !== 'unassigned',
      owner,
      'Set spec.owner to the accountable C4CI owner group.',
    ),
    createGapCheck(
      'TechDocs',
      hasTechDocs,
      readAnnotation(annotations, 'backstage.io/techdocs-ref'),
      'Publish docs and keep backstage.io/techdocs-ref on the catalog entity.',
    ),
    createGapCheck(
      'API',
      hasApi,
      hasApi ? `provides ${apiName}` : 'no API entity linked',
      'Register an API entity and link it through spec.providesApis.',
    ),
    createGapCheck(
      'Source',
      hasSource,
      readAnnotation(annotations, 'github.com/project-slug'),
      'Expose github.com/project-slug and backstage.io/source-location.',
    ),
    createGapCheck(
      'Deployment',
      hasDeployment,
      `${namespace} via ${workflow}`,
      'Declare the dev namespace and deployment workflow in project.yaml.',
    ),
    createGapCheck(
      'Health checks',
      hasHealth,
      `${healthEndpoint} / ${readyEndpoint}`,
      'Declare liveness and readiness endpoints in project.yaml.',
    ),
    createGapCheck(
      'Validation',
      validationCommands.length > 0,
      validationCommands.length > 0
        ? `${validationCommands.length} repo commands`
        : 'no validation commands declared',
      'Expose project.yaml tooling.commands through catalog annotations.',
    ),
    createGapCheck(
      'Kubernetes',
      hasKubernetes,
      hasDirectKubernetes
        ? readAnnotation(annotations, 'backstage.io/kubernetes-namespace')
        : runtimeChildComponents.join(', ') || runtimeMappingMode,
      'Add a direct AKS selector, or declare child runtime components when the product root is intentionally aggregate-only.',
    ),
    createGapCheck(
      'Lighthouse',
      hasLighthouse,
      hasLighthouseSource
        ? `source declared, live gate missing: ${lighthouseEvidence}`
        : lighthouseEvidence,
      'Add a real website URL annotation and c4ci.io/live-integration.lighthouse only after the real Lighthouse adapter is configured.',
    ),
    createGapCheck(
      'Incident/on-call',
      hasIncidentPath,
      hasIncidentPath
        ? 'live incident integration declared'
        : 'no live PagerDuty/Sentry gate',
      'Add PagerDuty or Sentry annotations plus c4ci.io/live-integration.pagerduty or c4ci.io/live-integration.sentry only after the real service exists.',
    ),
    createGapCheck(
      'Runbook',
      hasRunbook,
      readAnnotation(annotations, 'c4ci.io/runbook-url'),
      'Add a runbook URL owned by the product repo.',
    ),
    createGapCheck(
      'Dashboard',
      hasDashboard,
      readAnnotation(annotations, 'c4ci.io/dashboard-url'),
      'Add a real Grafana or equivalent dashboard URL.',
    ),
    createGapCheck(
      'Alerts',
      hasAlerts,
      readAnnotation(annotations, 'c4ci.io/alerts-url'),
      'Add a real alert policy or alert dashboard URL.',
    ),
    hasCostAdapter
      ? createGapCheck(
          'EUR cost adapter',
          true,
          readAnnotation(annotations, 'c4ci.io/cost-adapter-source'),
          'Keep spend mapped to product/project ownership in EUR.',
        )
      : createGatedCheck(
          'EUR cost adapter',
          'no live Azure billing export mapped by product/project',
          'Wire a C4CI-owned Azure billing adapter before showing spend charts.',
        ),
  ];

  const operationalLinks: OperationalLink[] = [
    {
      label: 'Catalog',
      to: `/catalog/default/component/${repoId}`,
      enabled: true,
    },
    {
      label: 'TechDocs',
      to: `/docs/default/component/${repoId}`,
      enabled: hasTechDocs,
    },
    {
      label: 'API',
      to: `/catalog/default/api/${apiName}`,
      enabled: hasApi,
    },
    {
      label: 'Kubernetes',
      to: kubernetesLink,
      enabled: hasKubernetes,
    },
    {
      label: 'Lighthouse',
      to: lighthouseLink,
      enabled: hasLighthouse,
    },
    {
      label: 'CI/CD',
      to: `/catalog/default/component/${repoId}/ci-cd`,
      enabled: hasSource,
    },
    {
      label: 'TODOs',
      to: `/catalog/default/component/${repoId}/todos`,
      enabled: hasSource,
    },
    {
      label: 'Scorecards',
      to: `/catalog/default/component/${repoId}/scorecards`,
      enabled: true,
    },
  ];

  return {
    id: repoId,
    apiName,
    title: readString(entity.metadata.title, repoId),
    description: readString(
      entity.metadata.description,
      'No product description declared in catalog.',
    ),
    owner,
    maturity: readAnnotation(annotations, 'c4ci.io/delivery-maturity'),
    maxFeatureClass: readAnnotation(annotations, 'c4ci.io/max-feature-class'),
    blueprintStatus: readAnnotation(annotations, 'c4ci.io/blueprint-status'),
    firstVerticalSlice: readAnnotation(
      annotations,
      'c4ci.io/first-vertical-slice-status',
    ),
    proofLevel: readAnnotation(annotations, 'c4ci.io/proof-level'),
    namespace,
    runtimeMappingMode,
    runtimeChildComponents,
    lighthouseAuditMode,
    lighthouseChildComponents,
    lighthouseEvidence,
    workflow,
    releaseMode: readAnnotation(annotations, 'c4ci.io/release-mode'),
    healthEndpoint,
    readyEndpoint,
    primaryController: readAnnotation(
      annotations,
      'c4ci.io/primary-controller',
    ),
    validationCommands,
    blockers: splitAnnotationList(
      readAnnotation(annotations, 'c4ci.io/blocking-gaps', ''),
    ),
    gapChecks,
    operationalLinks,
  };
}

function statusLabel(status: GapStatus): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'gated':
      return 'Adapter gated';
    default:
      return 'Missing';
  }
}

function statusColor(status: GapStatus): 'primary' | 'secondary' | 'default' {
  if (status === 'ready') {
    return 'primary';
  }

  if (status === 'gated') {
    return 'secondary';
  }

  return 'default';
}

function formatEuro(value: number): string {
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value);
}

const ProjectMetric = ({ label, value }: { label: string; value: string }) => {
  const classes = useStyles();

  return (
    <Box className={classes.metaItem}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

const GapStatusChip = ({ status }: { status: GapStatus }) => (
  <Chip
    color={statusColor(status)}
    label={statusLabel(status)}
    size="small"
    variant={status === 'missing' ? 'outlined' : 'default'}
  />
);

export const FactoryProjectInsightsPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [projects, setProjects] = useState<ProjectInsight[]>([]);
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [error, setError] = useState<string | undefined>();
  const [billingSummary, setBillingSummary] = useState<AzureBillingSummary>();
  const [billingState, setBillingState] = useState<
    'loading' | 'loaded' | 'error'
  >('loading');
  const [billingError, setBillingError] = useState<string | undefined>();

  useEffect(() => {
    let isActive = true;

    Promise.all([
      catalogApi.getEntities({
        filter: { kind: 'Component' },
        order: { field: 'metadata.name', order: 'asc' },
      }),
      catalogApi.getEntities({
        filter: { kind: 'API' },
        order: { field: 'metadata.name', order: 'asc' },
      }),
    ])
      .then(([componentResponse, apiResponse]) => {
        if (!isActive) {
          return;
        }

        const apiRepoIds = new Set(
          apiResponse.items
            .map(
              item => item.metadata.annotations?.['c4ci.io/registry-repo-id'],
            )
            .filter((repoId): repoId is string => Boolean(repoId)),
        );
        const catalogProjects = componentResponse.items
          .map(entity => projectEntityToInsight(entity, apiRepoIds))
          .filter((project): project is ProjectInsight => Boolean(project));

        if (catalogProjects.length === 0) {
          setProjects([]);
          setState('error');
          setError(
            'No registry-backed product components with project metadata annotations were returned.',
          );
          return;
        }

        setProjects(catalogProjects);
        setState('loaded');
        setError(undefined);
      })
      .catch(catalogError => {
        if (!isActive) {
          return;
        }

        setProjects([]);
        setState('error');
        setError(
          catalogError instanceof Error
            ? catalogError.message
            : String(catalogError),
        );
      });

    return () => {
      isActive = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    let isActive = true;

    discoveryApi
      .getBaseUrl('c4ci-azure-billing')
      .then(baseUrl => fetchApi.fetch(`${baseUrl}/v1/summary`))
      .then(async response => {
        if (!response.ok) {
          throw new Error(
            `Azure billing request failed with ${response.status}`,
          );
        }
        return (await response.json()) as AzureBillingSummary;
      })
      .then(summary => {
        if (!isActive) {
          return;
        }

        setBillingSummary(summary);
        setBillingState('loaded');
        setBillingError(undefined);
      })
      .catch(billingFetchError => {
        if (!isActive) {
          return;
        }

        setBillingSummary(undefined);
        setBillingState('error');
        setBillingError(
          billingFetchError instanceof Error
            ? billingFetchError.message
            : String(billingFetchError),
        );
      });

    return () => {
      isActive = false;
    };
  }, [discoveryApi, fetchApi]);

  const blockerCount = projects.reduce(
    (total, project) => total + project.blockers.length,
    0,
  );
  const readyCount = projects.reduce(
    (total, project) =>
      total +
      project.gapChecks.filter(check => check.status === 'ready').length,
    0,
  );
  const missingCount = projects.reduce(
    (total, project) =>
      total +
      project.gapChecks.filter(check => check.status === 'missing').length,
    0,
  );
  const gatedCount = projects.reduce(
    (total, project) =>
      total +
      project.gapChecks.filter(check => check.status === 'gated').length,
    0,
  );
  const connectedWorkflows = projects.filter(project =>
    isDeclared(project.workflow),
  ).length;
  const totalAllocatedCost =
    billingSummary?.products.reduce(
      (total, product) => total + product.allocatedCost,
      0,
    ) ?? 0;

  return (
    <Page themeId="home">
      <Header
        title="Project Work Insights"
        subtitle="Catalog-backed product gap dashboard, operational proof, and adapter gates from live product metadata"
      />
      <Content>
        <Box className={classes.hero}>
          <div className={classes.chipRow}>
            <Chip color="primary" label="Project view" />
            <Chip
              label={
                state === 'loaded'
                  ? 'Live catalog data'
                  : 'Catalog data required'
              }
              variant="outlined"
            />
            <Chip label="EUR-ready" variant="outlined" />
            <Chip label="Real Azure EUR spend" variant="outlined" />
          </div>
          <Typography variant="h4" component="h2" gutterBottom>
            Catalog-backed product gap dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Factory now shows product readiness from catalog truth only: docs,
            API, source, deployment, health, validation, Kubernetes, CI/CD,
            scorecards, TODO scanning, runbooks, alerts, dashboards, and
            incident/on-call status. Real adapter gates stay visible when a live
            provider is not connected.
          </Typography>
        </Box>

        {state === 'loading' && <Progress />}
        {error && (
          <Box mb={3}>
            <InfoCard title="Live Catalog Data Required">
              <Typography variant="body2" color="textSecondary">
                Project insights did not render product cards because live
                catalog metadata was unavailable: {error}
              </Typography>
            </InfoCard>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <InfoCard className={classes.statCard} title="Tracked Projects">
              <Box display="flex" alignItems="center" mb={2}>
                <AssessmentIcon color="primary" />
              </Box>
              <Typography variant="h3">{projects.length}</Typography>
              <Typography variant="body2" color="textSecondary">
                Registry-backed projects returned by the live catalog.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoCard className={classes.statCard} title="Workflow Coverage">
              <Box display="flex" alignItems="center" mb={2}>
                <TimelineIcon color="primary" />
              </Box>
              <Typography variant="h3">
                {connectedWorkflows}/{projects.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Projects declaring a deployment workflow in product metadata.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoCard className={classes.statCard} title="Readiness Gaps">
              <Typography variant="h3">{missingCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Missing proof items computed from catalog annotations.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoCard className={classes.statCard} title="Adapter Gates">
              <Box display="flex" alignItems="center" mb={2}>
                <EuroIcon color="primary" />
              </Box>
              <Typography variant="h3">{gatedCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Real adapters required before charts or external integrations
                appear.
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>

        <Box mt={4}>
          <InfoCard title="Azure Billing Adapter">
            {billingState === 'loading' && <Progress />}
            {billingState === 'error' && (
              <Typography variant="body2" color="textSecondary">
                Real Azure billing data is unavailable from the Factory backend:{' '}
                {billingError}
              </Typography>
            )}
            {billingState === 'loaded' && billingSummary && (
              <>
                <div className={classes.chipRow}>
                  <Chip
                    color={
                      billingSummary.state === 'ready'
                        ? 'primary'
                        : 'secondary'
                    }
                    label={billingSummary.state}
                    size="small"
                  />
                  <Chip label={billingSummary.currency} size="small" />
                  <Chip label={billingSummary.source} size="small" />
                  <Chip
                    label={billingSummary.allocation.method}
                    size="small"
                    variant="outlined"
                  />
                </div>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Real Azure EUR spend from C4CI resource groups, normalized to
                  explicit product ownership first; shared platform groups use
                  live Kubernetes namespace request share.
                  Period: {billingSummary.period.from || 'not available'} to{' '}
                  {billingSummary.period.to || 'not available'}.
                </Typography>
                <div className={classes.metaGrid}>
                  <ProjectMetric
                    label="Allocated Spend"
                    value={formatEuro(totalAllocatedCost)}
                  />
                  <ProjectMetric
                    label="Resource Groups"
                    value={String(billingSummary.resourceGroups.length)}
                  />
                  <ProjectMetric
                    label="Unallocated"
                    value={formatEuro(billingSummary.unallocated.cost)}
                  />
                  <ProjectMetric
                    label="Generated"
                    value={new Date(
                      billingSummary.generatedAt,
                    ).toLocaleString()}
                  />
                </div>
                {billingSummary.warnings.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">
                      Adapter warnings
                    </Typography>
                    <div className={classes.list}>
                      {billingSummary.warnings.map(warning => (
                        <Box className={classes.listItem} key={warning}>
                          <Typography variant="body2" color="textSecondary">
                            {warning}
                          </Typography>
                        </Box>
                      ))}
                    </div>
                  </Box>
                )}
              </>
            )}
          </InfoCard>
        </Box>

        <Box mt={4}>
          <ContentHeader title="Spend By Product" />
          <Grid container spacing={3}>
            {(billingSummary?.products ?? []).map(product => (
              <Grid item xs={12} md={4} key={product.productId}>
                <InfoCard
                  className={classes.statCard}
                  title={product.title}
                >
                  <div className={classes.chipRow}>
                    <Chip label={product.productId} size="small" />
                    <Chip label={product.namespace} size="small" />
                  </div>
                  <Typography variant="h4">
                    {formatEuro(product.allocatedCost)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Allocation weight:{' '}
                    {(product.allocationWeight * 100).toFixed(1)}%
                  </Typography>
                  <div className={classes.metaGrid}>
                    <ProjectMetric
                      label="CPU requests"
                      value={`${product.cpuCores.toFixed(3)} cores`}
                    />
                    <ProjectMetric
                      label="Memory requests"
                      value={`${product.memoryGiB.toFixed(3)} GiB`}
                    />
                    <ProjectMetric
                      label="Pods"
                      value={String(product.podCount)}
                    />
                  </div>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mt={4}>
          <ContentHeader title="Work By Project" />
          <Grid container spacing={3}>
            {projects.map(project => {
              const projectReadyCount = project.gapChecks.filter(
                check => check.status === 'ready',
              ).length;
              const projectMissing = project.gapChecks.filter(
                check => check.status !== 'ready',
              );

              return (
                <Grid item xs={12} lg={6} key={project.id}>
                  <InfoCard
                    className={classes.projectCard}
                    title={project.title}
                  >
                    <div className={classes.chipRow}>
                      <Chip label={project.id} size="small" />
                      <Chip label={project.owner} size="small" />
                      <Chip
                        color="primary"
                        label={project.maturity}
                        size="small"
                      />
                      <Chip
                        label={`${projectReadyCount}/${project.gapChecks.length} ready`}
                        size="small"
                        variant="outlined"
                      />
                    </div>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {project.description}
                    </Typography>
                    <div className={classes.metaGrid}>
                      <ProjectMetric
                        label="Feature Class"
                        value={project.maxFeatureClass}
                      />
                      <ProjectMetric
                        label="Blueprint"
                        value={project.blueprintStatus}
                      />
                      <ProjectMetric
                        label="Vertical Slice"
                        value={project.firstVerticalSlice}
                      />
                      <ProjectMetric label="Proof" value={project.proofLevel} />
                      <ProjectMetric
                        label="Namespace"
                        value={project.namespace}
                      />
                      <ProjectMetric
                        label="Runtime Mapping"
                        value={project.runtimeMappingMode}
                      />
                      <ProjectMetric
                        label="Lighthouse"
                        value={project.lighthouseEvidence}
                      />
                      <ProjectMetric
                        label="Workflow"
                        value={project.workflow}
                      />
                      <ProjectMetric
                        label="Primary Controller"
                        value={project.primaryController}
                      />
                      <ProjectMetric
                        label="Release Mode"
                        value={project.releaseMode}
                      />
                      <ProjectMetric
                        label="Health"
                        value={`${project.healthEndpoint} / ${project.readyEndpoint}`}
                      />
                    </div>

                    <Box mt={3}>
                      <Typography variant="subtitle2">
                        Required Work From Catalog Truth
                      </Typography>
                      <div className={classes.gapGrid}>
                        {projectMissing.length > 0 ? (
                          projectMissing.map(check => (
                            <Box className={classes.gapItem} key={check.label}>
                              <div className={classes.chipRow}>
                                <GapStatusChip status={check.status} />
                                <Chip label={check.label} size="small" />
                              </div>
                              <Typography variant="body2" color="textSecondary">
                                Evidence: {check.evidence}
                              </Typography>
                              <Typography variant="body2">
                                Required: {check.requiredWork}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Box className={classes.gapItem}>
                            <Typography variant="body2" color="textSecondary">
                              All current catalog-backed checks are ready.
                            </Typography>
                          </Box>
                        )}
                      </div>
                    </Box>

                    <Box mt={3}>
                      <Typography variant="subtitle2">
                        Operational Proof
                      </Typography>
                      <div className={classes.linkRow}>
                        {project.operationalLinks.map(link =>
                          link.enabled ? (
                            <Link
                              component={RouterLink}
                              key={link.label}
                              to={link.to}
                            >
                              {link.label}
                            </Link>
                          ) : (
                            <Typography
                              color="textSecondary"
                              key={link.label}
                              variant="body2"
                            >
                              {link.label}: not connected
                            </Typography>
                          ),
                        )}
                      </div>
                    </Box>

                    <Box mt={3}>
                      <Typography variant="subtitle2">
                        Declared blockers ({project.blockers.length})
                      </Typography>
                      <div className={classes.list}>
                        {project.blockers.length > 0 ? (
                          project.blockers.map(item => (
                            <Box className={classes.listItem} key={item}>
                              <Typography variant="body2" color="textSecondary">
                                {item}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Box className={classes.listItem}>
                            <Typography variant="body2" color="textSecondary">
                              No blocking gaps declared in product metadata.
                            </Typography>
                          </Box>
                        )}
                      </div>
                    </Box>
                  </InfoCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box mt={4}>
          <InfoCard title="Real Adapter Gates">
            <Typography variant="body2" color="textSecondary" paragraph>
              Cost, incident/on-call, alerting, and dashboard integrations only
              render as live surfaces after product entities declare real
              provider annotations plus c4ci.io/live-integration.* gates.
              Until then, Factory shows the missing proof instead of drawing
              non-authoritative panels.
            </Typography>
            <Typography variant="body2">
              Current declared blockers: {blockerCount}. Ready checks:{' '}
              {readyCount}. Missing checks: {missingCount}. Adapter-gated
              checks: {gatedCount}. Spend reporting must come from a C4CI-owned
              Azure billing export, mapped to product/project ownership, and
              shown in EUR.
            </Typography>
          </InfoCard>
        </Box>
      </Content>
    </Page>
  );
};
